import type { DamageType } from "./combat.js";
import type { Position } from "./grid.js";

/** D&D 5e ability scores. */
export interface AbilityScores {
  STR: number;
  DEX: number;
  CON: number;
  INT: number;
  WIS: number;
  CHA: number;
}

/** Returns the standard 5e modifier for a given ability score. */
export function abilityModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

/** Speed block matching the SRD JSON shape. */
export interface Speed {
  walk?: number;
  swim?: number;
  fly?: number;
  climb?: number;
  burrow?: number;
}

/** Base interface shared by every entity in the world. */
export interface Entity {
  /** Unique instance ID assigned at spawn time. */
  readonly instanceId: string;
  /** Reference to the SRD data record (e.g. "aboleth", "knight"). */
  readonly dataId: string;
  position: Position;
  hp: number;
  maxHp: number;
  armorClass: number;
  speed: Speed;
  abilities: AbilityScores;
  /**
   * Damage types this entity takes half damage from (×0.5, rounded down).
   * Resolved by `applyDamage` (B02). Omitted = no resistances.
   */
  resistances?: readonly DamageType[];
  /**
   * Damage types this entity takes double damage from (×2). Resolved by
   * `applyDamage` (B02). Omitted = no vulnerabilities.
   */
  vulnerabilities?: readonly DamageType[];
  /**
   * Damage types this entity takes zero damage from (×0). Overrides any
   * resistance or vulnerability on the same type. Omitted = no immunities.
   */
  immunities?: readonly DamageType[];
}

// ── Enemy perception + faction types (E01) ───────────────────────────────────

/** Three factions; controls engagement and HUD colour. */
export type EnemyFaction = "hostile" | "neutral" | "friendly";

/** Physical size class. v1 = all medium; v2 adds pack (small) and multi-cell (large). */
export type MonsterSize = "small" | "medium" | "large";

/** Forward sight cone parameters. */
export interface SightPerception {
  /** Cone half-angle in degrees (spec default: 60°, i.e. 120° total). */
  cone: number;
  /** Maximum sight range in cells (spec default: 8). */
  range: number;
}

/** Omnidirectional hearing parameters. */
export interface HearingPerception {
  /** Hearing radius in cells (spec default: 3 baseline). */
  radius: number;
}

// ── Default perception values (use these at spawn when SRD data omits them) ──

export const DEFAULT_SIGHT: SightPerception = { cone: 60, range: 8 };
export const DEFAULT_HEARING: HearingPerception = { radius: 3 };

/** A fully resolved monster entity (data + runtime state). */
/**
 * Runtime attack block (EV-E1), parsed from the monster record's `attack`
 * field at build time. Absent = the legacy 1d6 slashing melee.
 */
export interface MonsterAttack {
  readonly diceCount: number;
  readonly diceSides: number;
  /** Flat damage bonus baked into the dice notation (e.g. the +2 in 1d10+2). */
  readonly modifier: number;
  readonly damageType: DamageType;
  readonly ranged: boolean;
  /** Max attack distance in cells. 1 for melee. */
  readonly range: number;
  /** Stat used for to-hit and damage mods. */
  readonly ability: "STR" | "DEX";
  /** EV-E4 — chance-based condition rider on hit ("shock baton" rule). */
  readonly conditionOnHit?: { readonly id: ConditionId; readonly chance: number };
}

export interface MonsterEntity extends Entity {
  readonly kind: "monster";
  readonly name: string;
  readonly challengeRating: number;
  /** Authored attack profile (EV-E1). Absent = legacy 1d6 slashing melee. */
  attack?: MonsterAttack;
  /** Three.js sprite instance ID for the renderer to look up. */
  spriteId: string | null;

  // ── E01: AI + content fields ───────────────────────────────────────────────
  faction: EnemyFaction;
  size: MonsterSize;
  /** Home cell — the enemy wanders within leashRadius of this point. */
  anchor: Position;
  /** Patrol radius in cells. 0 = stationary (guards, bosses). */
  leashRadius: number;
  sight: SightPerception;
  hearing: HearingPerception;
  /** Spell IDs this enemy can cast (empty array = non-caster). */
  spells: readonly string[];
  /** Reaction IDs enabled for this enemy (empty array = none). */
  reactions: readonly string[];
  /** Reference to a loot-table record spawned on death (null = no drops). */
  lootTableId: string | null;
  /**
   * XP awarded to the party on death (W07). Authored on the monster data
   * record; conventionally follows the SRD CR→XP table (CR 1/4 → 50,
   * CR 1 → 200, CR 2 → 450, …) but adventure authors are free to tune.
   * `0` means no award.
   */
  xp: number;
}

/** A single party member controlled by the player. */
export interface PlayerCharacter extends Entity {
  readonly kind: "player";
  readonly name: string;
  readonly characterClass: string;
  readonly level: number;
  proficiencyBonus: number;
  /** Remaining spell slots keyed by spell level. */
  spellSlots: Record<number, number>;
  /**
   * Accumulated experience points. Optional on the MVP stub type;
   * promoted to a required field on `Character` (C01) and mutated by
   * `PartyStore.awardXp` (W08).
   */
  xp?: number;
  /**
   * SRD spell slugs the character has learned. Optional on the MVP stub
   * type; promoted to a required field on `Character` (S01) and mutated
   * by `PartyStore.addSpell / removeSpell`.
   */
  knownSpells?: string[];
  /**
   * Reaction IDs the player has toggled on. Optional on the MVP stub;
   * promoted to required on `Character` (B05). A missing field is
   * treated as `[]` by the reaction matcher.
   */
  reactions?: readonly string[];
}

/** An NPC that can be interacted with but is not hostile. */
export interface NPCEntity extends Entity {
  readonly kind: "npc";
  readonly name: string;
  /** Ink knot to begin when the player clicks this NPC. */
  readonly inkKnot: string;
  spriteId: string | null;
}

export type AnyEntity = MonsterEntity | PlayerCharacter | NPCEntity;

// ── Skill and ability names ────────────────────────────────────────────────────

export type AbilityName = keyof AbilityScores;

export const SKILL_NAMES = [
  "Acrobatics",
  "Animal Handling",
  "Arcana",
  "Athletics",
  "Deception",
  "History",
  "Insight",
  "Intimidation",
  "Investigation",
  "Medicine",
  "Nature",
  "Perception",
  "Performance",
  "Persuasion",
  "Religion",
  "Sleight of Hand",
  "Stealth",
  "Survival",
] as const;

export type SkillName = (typeof SKILL_NAMES)[number];

/** SRD mapping: each skill is governed by one ability score. */
export const SKILL_ABILITY: Readonly<Record<SkillName, AbilityName>> = {
  Acrobatics: "DEX",
  "Animal Handling": "WIS",
  Arcana: "INT",
  Athletics: "STR",
  Deception: "CHA",
  History: "INT",
  Insight: "WIS",
  Intimidation: "CHA",
  Investigation: "INT",
  Medicine: "WIS",
  Nature: "INT",
  Perception: "WIS",
  Performance: "CHA",
  Persuasion: "CHA",
  Religion: "INT",
  "Sleight of Hand": "DEX",
  Stealth: "DEX",
  Survival: "WIS",
};

// ── Conditions ────────────────────────────────────────────────────────────────

export type ConditionId =
  // 5e SRD core conditions
  | "blinded"
  | "charmed"
  | "deafened"
  | "exhaustion"
  | "frightened"
  | "grappled"
  | "incapacitated"
  | "invisible"
  | "paralyzed"
  | "petrified"
  | "poisoned"
  | "prone"
  | "restrained"
  | "stunned"
  | "unconscious"
  // bkcrawl-flavoured additions used by combat-physical's v1 condition list
  // (B03) and the trap spec.
  | "bleeding"
  | "blessed"
  | "haste"
  | "strong"
  | "silence"
  | "sleep"
  | "slow"
  | "burning"
  | "disease";

// ── Death saves ───────────────────────────────────────────────────────────────

export interface DeathSaveState {
  /** Number of successful death saves this combat (0–3). */
  readonly successes: number;
  /** Number of failed death saves this combat (0–3). */
  readonly failures: number;
  /** True once the character has 3 successes; stops rolling until healed. */
  readonly stable: boolean;
}

/** Blank death-save state for a healthy (or freshly revived) character. */
export const DEATH_SAVE_NONE: DeathSaveState = { successes: 0, failures: 0, stable: false };

// ── Formation slot ────────────────────────────────────────────────────────────

/** 0 = front row, 1 = back row. */
export type FormationRow = 0 | 1;
/** 0 = left column, 1 = right column. */
export type FormationCol = 0 | 1;

/** Position in the 2×2 party formation grid. */
export interface FormationSlot {
  readonly row: FormationRow;
  readonly col: FormationCol;
}

// ── Identifier aliases (resolved to data records in C02) ─────────────────────

/** SRD race slug, e.g. `"human"`, `"elf"`, `"half-orc"`. */
export type RaceId = string;
/** SRD class slug, e.g. `"fighter"`, `"wizard"`, `"rogue"`. */
export type ClassId = string;
/** SRD or LLM-custom background slug, e.g. `"soldier"`, `"sage"`. */
export type BackgroundId = string;

// ── Character ─────────────────────────────────────────────────────────────────

/**
 * Full player character — extends `PlayerCharacter` with identity, proficiencies,
 * and per-session state required by C04+ downstream tasks.
 *
 * Mutable fields (hp, xp, conditions, deathSaves, formationSlot, spellSlots)
 * represent transient session state that changes during play.
 * Readonly fields represent identity/progression that only changes on level-up
 * or character creation.
 */
export interface Character extends PlayerCharacter {
  // Identity
  readonly race: RaceId;
  readonly background: BackgroundId;
  /** Data-URL or absolute path to portrait image; null until generated (L04). */
  readonly portrait: string | null;

  // Progression
  xp: number;

  // Proficiencies (set at character creation, may expand on level-up)
  readonly skillProficiencies: ReadonlySet<SkillName>;
  readonly saveProficiencies: ReadonlySet<AbilityName>;
  /** Skills where proficiency bonus is doubled (Rogue/Bard expertise). */
  readonly expertiseSkills: ReadonlySet<SkillName>;

  /**
   * EV-E7 — spellcasting stat for custom (renamed) classes whose slug
   * isn't in the legacy `CLASS_PRIMARY_ABILITY` table. Absent = resolve
   * from the legacy table (INT fallback).
   */
  readonly castingAbility?: AbilityName;

  // Spells (S01)
  /** SRD spell slugs this character has learned. Grows via level-up or scroll transcription. */
  knownSpells: string[];

  /**
   * Reaction IDs the player has toggled on for this character (B05). Each
   * entry is an ID registered in `ReactionSystem`; trigger matching runs
   * on this list after every combat event targeting the character. Empty
   * = no reactions active.
   */
  reactions: readonly string[];

  // Per-session state
  conditions: ReadonlySet<ConditionId>;
  deathSaves: DeathSaveState;
  /** Null until party formation is assigned (C05). */
  formationSlot: FormationSlot | null;
}

// ── Party ─────────────────────────────────────────────────────────────────────

/** A fixed four-member adventuring party. Formation swap API added in C05. */
export interface Party {
  readonly members: readonly [Character, Character, Character, Character];
}
